import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { FacilityIdSchema, FacilityPermissionLevelSchema } from "./facility.contract.js";
/**
 * Manage roles — the READ half (ADR-030 Phase A). Roles are Keycloak client roles mirrored into `role`; the "group" is
 * pure string surgery on the name (everything before the LAST "-"), never a column. `editable` / `readOnlyReason` are
 * COMPUTED SERVER-SIDE from the ADR-030 §3.3 rules — the UI never derives authz from `hpfp`. The list is SCOPED
 * server-side: a SuperAdmin gets every role, anyone else only the roles whose name starts with one of their manage
 * prefixes (the legacy `?rolesWithPrefix=manage`). A role outside the scope is a 404, never a 403 (no existence leak).
 */
const c = initContract();
const UnauthorizedSchema = z.object({ message: z.string() });
/** The caller may not administer any role (no SuperAdmin, no manage prefix) — the legacy `hasAccessManagementRights`. */
const RolesForbiddenSchema = z.object({ message: z.string(), code: z.literal("ROLES_FORBIDDEN") });
const RoleNotFoundSchema = z.object({ message: z.string(), code: z.literal("ROLE_NOT_FOUND") });
export const RoleReadOnlyReasonSchema = z.enum(["SUPER_ADMIN", "HIDDEN", "ADMIN_ROLE", "OWN_ROLE"]);
export const RoleCardSchema = z.object({
    id: z.number().int(),
    /** The full Keycloak role name — the editor's title. */
    name: z.string(),
    /** Everything before the LAST "-"; null ⇒ the "No group" bucket (the legacy "ohne Gruppe"). */
    prefix: z.string().nullable(),
    /** After the last "-" (the tree's button label); the full name when there is no dash. */
    shortName: z.string(),
    /** The highest possible facility permission — a CAP on grants, not a grant. null = never configured (the legacy
     *  "level 0" warning); the engine reads null as UNCAPPED, which is why Phase B never writes it back. */
    hpfp: FacilityPermissionLevelSchema.nullable(),
    hidden: z.boolean(),
    superAdmin: z.boolean(),
    /** The legacy "Grund-Ordner": a navigation start node for the role's users, NOT an access boundary. 0 ⇒ null. */
    userRootFacilityId: z.number().int().positive().nullable(),
    /** ADR-030 §3.3, server-computed. */
    editable: z.boolean(),
    readOnlyReason: RoleReadOnlyReasonSchema.nullable(),
    /** ISO-8601 UTC with milliseconds — the concurrency token every PATCH sends back (opaque to the client; a second-
     *  precision token could not tell two writes within the same second apart). */
    updatedAt: z.string(),
    /** How many facilities carry a grant of this role (Phase D): the legacy dashboard's "no facilities assigned" hint
     *  becomes a marker in the tree. 0 with a level set ⇒ the role can see nothing yet. */
    grantCount: z.number().int().nonnegative(),
});
/** One user holding the role — READ-ONLY (ADR-030 §5 #2 default: membership is Keycloak's; the list is from `user_role`). */
export const RoleMemberSchema = z.object({ userId: z.number().int(), name: z.string() });
export const RoleMembersSchema = z.object({ members: z.array(RoleMemberSchema) });
export const RolesListSchema = z.object({
    roles: z.array(RoleCardSchema),
    caller: z.object({
        isSuper: z.boolean(),
        /** The manage prefixes of the caller's roles (a multi-role caller may hold several) — informational. */
        managePrefixes: z.array(z.string()),
    }),
});
export const RoleGrantSchema = z.object({
    facilityId: z.number().int(),
    /** The raw row. */
    level: FacilityPermissionLevelSchema,
    /** `LEAST(level, COALESCE(hpfp, 'access_manage'))` — exactly what the engine grants; a grant above a lowered HPFP
     *  renders as capped, never re-saved lower. */
    effectiveLevel: FacilityPermissionLevelSchema,
});
export const RoleGrantsSchema = z.object({
    grants: z.array(RoleGrantSchema),
    /** Facilities with at least one grant of this role STRICTLY below them (the legacy `roleHasPermissionsOnChildren`). */
    facilityIdsWithChildGrants: z.array(z.number().int()),
});
/** A role id path param, bounded to int4 like `FacilityIdSchema`. */
export const RoleIdSchema = FacilityIdSchema;
/**
 * `PATCH /roles/:id` (ADR-030 Phase B). `hpfp` is a LEVEL, never null: the engine reads a NULL cap as UNCAPPED, so
 * "clearing" it would silently re-arm any retained access_manage grant — a never-configured role can only be RAISED.
 * The two prefix columns are never in the body: derived server-side from the new level exactly as the legacy did
 * (≥ Review ⇒ the control prefix, ≥ Admin ⇒ the manage prefix). `hidden` and `userRootFacilityId` are SuperAdmin-only.
 * `expectedUpdatedAt` is the card's `updatedAt` — a moved role is a 409 `ROLE_STALE`, never a silent overwrite.
 */
export const PatchRoleBodySchema = z
    .object({
    hpfp: FacilityPermissionLevelSchema.optional(),
    hidden: z.boolean().optional(),
    userRootFacilityId: FacilityIdSchema.nullable().optional(),
    expectedUpdatedAt: z.string().min(1),
})
    .refine((b) => b.hpfp !== undefined || b.hidden !== undefined || b.userRootFacilityId !== undefined, { message: "nothing to change" });
const RoleWriteForbiddenSchema = z.object({
    message: z.string(),
    /** ROLES_FORBIDDEN = the entry gate; ROLE_NOT_EDITABLE = §3.3 #3 (own / admin / hidden / super role);
     *  ROLE_FIELD_SUPER_ONLY = `hidden` / `userRootFacilityId` by a non-SuperAdmin. */
    code: z.enum(["ROLES_FORBIDDEN", "ROLE_NOT_EDITABLE", "ROLE_FIELD_SUPER_ONLY"]),
});
const RoleWriteNotFoundSchema = z.object({ message: z.string(), code: z.enum(["ROLE_NOT_FOUND", "FACILITY_NOT_FOUND"]) });
const RoleWriteConflictSchema = z.object({
    message: z.string(),
    /** ROLE_STALE = the token moved; HPFP_SUPER_ONLY = a non-SuperAdmin raising a role to Admin. */
    code: z.enum(["ROLE_STALE", "HPFP_SUPER_ONLY"]),
});
/**
 * `PUT /roles/:id/grants/:facilityId` (ADR-030 Phase C). `level` ≤ the role's HPFP (409 `GRANT_ABOVE_HPFP`); an Admin
 * grant is a SuperAdmin's to give (409 `GRANT_SUPER_ONLY`). `applyToChildren` writes the subtree the caller ALSO
 * administers and NEVER lowers a descendant's existing grant (`GREATEST`); lowering is only ever the explicit single-node
 * PUT. The response says exactly which facilities changed.
 */
export const PutGrantBodySchema = z.object({ level: FacilityPermissionLevelSchema, applyToChildren: z.boolean().default(false) });
export const GrantWriteResultSchema = z.object({ affected: z.number().int(), facilityIds: z.array(z.number().int()) });
const GrantForbiddenSchema = z.object({
    message: z.string(),
    /** ROLES_FORBIDDEN = the entry gate; ROLE_NOT_EDITABLE = §3.3 #3; FACILITY_FORBIDDEN = no effective Admin on the target. */
    code: z.enum(["ROLES_FORBIDDEN", "ROLE_NOT_EDITABLE", "FACILITY_FORBIDDEN"]),
});
const GrantNotFoundSchema = z.object({ message: z.string(), code: z.enum(["ROLE_NOT_FOUND", "FACILITY_NOT_FOUND"]) });
const GrantConflictSchema = z.object({ message: z.string(), code: z.enum(["GRANT_ABOVE_HPFP", "GRANT_SUPER_ONLY", "ROLE_HPFP_UNSET"]) });
export const roleContract = c.router({
    putGrant: {
        method: "PUT",
        path: "/roles/:id/grants/:facilityId",
        pathParams: z.object({ id: RoleIdSchema, facilityId: FacilityIdSchema }),
        body: PutGrantBodySchema,
        responses: { 200: GrantWriteResultSchema, 400: z.object({ message: z.string() }), 401: UnauthorizedSchema, 403: GrantForbiddenSchema, 404: GrantNotFoundSchema, 409: GrantConflictSchema },
        summary: "Grant a role a level on a facility (optionally its subtree, never lowering); audited per facility (ADR-030 Phase C).",
    },
    deleteGrant: {
        method: "DELETE",
        path: "/roles/:id/grants/:facilityId",
        pathParams: z.object({ id: RoleIdSchema, facilityId: FacilityIdSchema }),
        query: z.object({ applyToChildren: z.enum(["true", "false"]).optional() }),
        body: z.object({}).optional(),
        responses: { 200: GrantWriteResultSchema, 401: UnauthorizedSchema, 403: GrantForbiddenSchema, 404: GrantNotFoundSchema },
        summary: "Revoke a role's grant on a facility (optionally its subtree the caller administers); audited per facility.",
    },
    listRoles: {
        method: "GET",
        path: "/roles",
        responses: { 200: RolesListSchema, 401: UnauthorizedSchema, 403: RolesForbiddenSchema },
        summary: "The roles the caller may administer (SuperAdmin: all), with server-computed editability (ADR-030 §3.3).",
    },
    patchRole: {
        method: "PATCH",
        path: "/roles/:id",
        pathParams: z.object({ id: RoleIdSchema }),
        body: PatchRoleBodySchema,
        responses: { 200: RoleCardSchema, 400: z.object({ message: z.string() }), 401: UnauthorizedSchema, 403: RoleWriteForbiddenSchema, 404: RoleWriteNotFoundSchema, 409: RoleWriteConflictSchema },
        summary: "Change a role's highest level (prefixes derived), hidden flag or base folder (ADR-030 Phase B; audited).",
    },
    getRoleGrants: {
        method: "GET",
        path: "/roles/:id/grants",
        pathParams: z.object({ id: RoleIdSchema }),
        responses: { 200: RoleGrantsSchema, 401: UnauthorizedSchema, 403: RolesForbiddenSchema, 404: RoleNotFoundSchema },
        summary: "One role's facility grants (raw + effective level) and the facilities with grants below (ADR-030 Phase A).",
    },
    getRoleMembers: {
        method: "GET",
        path: "/roles/:id/members",
        pathParams: z.object({ id: RoleIdSchema }),
        responses: { 200: RoleMembersSchema, 401: UnauthorizedSchema, 403: RolesForbiddenSchema, 404: RoleNotFoundSchema },
        summary: "The users holding one role, read-only, from `user_role` (ADR-030 Phase D); same scope as the grants read.",
    },
});
//# sourceMappingURL=role.contract.js.map